/**
 * Keyboard Shortcuts Hook
 * Bloomberg Terminal style: Power-user keyboard navigation
 * Implements global keyboard shortcuts for productivity
 */

import { useEffect, useCallback, useRef } from 'react';

export interface KeyboardShortcut {
  /**
   * Keyboard key (e.g., 'k', 's', 'u', 'Escape')
   */
  key: string;

  /**
   * Modifier keys required
   */
  modifiers?: {
    ctrl?: boolean;
    meta?: boolean; // Command on Mac, Windows key on Windows
    shift?: boolean;
    alt?: boolean;
  };

  /**
   * Callback to execute when shortcut is triggered
   */
  handler: (event: KeyboardEvent) => void;

  /**
   * Description for UI display
   */
  description?: string;

  /**
   * Whether shortcut is enabled
   */
  enabled?: boolean;
}

export interface ChordShortcut {
  /**
   * Key sequence (e.g., ['g', 'd'] for g → d)
   */
  sequence: readonly string[];

  /**
   * Callback to execute when chord is completed
   */
  handler: (event: KeyboardEvent) => void;

  /**
   * Description for UI display
   */
  description?: string;

  /**
   * Whether shortcut is enabled
   */
  enabled?: boolean;
}

export interface UseKeyboardShortcutsOptions {
  /**
   * Array of shortcuts to register
   */
  shortcuts?: KeyboardShortcut[];

  /**
   * Array of chord (multi-key sequence) shortcuts to register
   */
  chords?: ChordShortcut[];

  /**
   * Whether to prevent default behavior
   */
  preventDefault?: boolean;

  /**
   * Whether shortcuts are enabled globally
   */
  enabled?: boolean;
}

/** Time in ms between chord keystrokes before buffer resets */
const CHORD_TIMEOUT_MS = 800;

/**
 * Returns true if the event target is a focusable text input (input, textarea, contenteditable).
 * Chord shortcuts must not fire when the user is typing.
 */
function isTypingTarget(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null;
  if (!target) return false;
  if (!target.tagName) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  // isContentEditable can return false in some environments (e.g. jsdom) even when
  // the attribute is set; check the attribute directly as a fallback.
  if (target.isContentEditable) return true;
  const ceAttr = target.getAttribute('contenteditable');
  if (ceAttr !== null && ceAttr !== 'false') return true;
  return false;
}

/**
 * Hook for managing global keyboard shortcuts with optional chord sequence support.
 * @param options - Configuration options
 * @example
 * ```tsx
 * useKeyboardShortcuts({
 *   shortcuts: [
 *     {
 *       key: 'k',
 *       modifiers: { meta: true, ctrl: true },
 *       handler: () => openCommandPalette(),
 *       description: 'Open command palette',
 *     },
 *   ],
 *   chords: [
 *     { sequence: ['g', 'd'], handler: () => navigate('/dashboard'), description: 'Go to Dashboard' },
 *   ],
 * });
 * ```
 */
export function useKeyboardShortcuts(options?: UseKeyboardShortcutsOptions) {
  const {
    shortcuts = [],
    chords = [],
    preventDefault = true,
    enabled = true,
  } = options || {};

  // Pending chord buffer: keys pressed so far in the current chord attempt
  const chordBufferRef = useRef<string[]>([]);
  const chordTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearChordBuffer = useCallback(() => {
    chordBufferRef.current = [];
    if (chordTimeoutRef.current !== null) {
      clearTimeout(chordTimeoutRef.current);
      chordTimeoutRef.current = null;
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Check each registered shortcut (modifier-based)
      for (const shortcut of shortcuts) {
        if (shortcut.enabled === false) continue;

        // Skip shortcuts without a key defined
        if (!shortcut.key) continue;

        // Check if key matches (event.key can be undefined for IME/composition events)
        if (!event.key) continue;
        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
        if (!keyMatches) continue;

        // Check if modifiers match
        // Cross-platform pattern: if both meta and ctrl are true in modifiers,
        // match if EITHER is pressed (Meta on Mac, Ctrl on Windows/Linux)
        const modifiers = shortcut.modifiers || {};
        const bothMetaAndCtrl = modifiers.meta && modifiers.ctrl;
        const ctrlMatches = bothMetaAndCtrl
          ? event.ctrlKey || event.metaKey
          : modifiers.ctrl
            ? event.ctrlKey
            : !event.ctrlKey;
        const metaMatches = bothMetaAndCtrl
          ? event.ctrlKey || event.metaKey
          : modifiers.meta
            ? event.metaKey
            : !event.metaKey;
        const shiftMatches = modifiers.shift ? event.shiftKey : !event.shiftKey;
        const altMatches = modifiers.alt ? event.altKey : !event.altKey;

        if (ctrlMatches && metaMatches && shiftMatches && altMatches) {
          if (preventDefault) {
            event.preventDefault();
          }
          shortcut.handler(event);
          return;
        }
      }

      // Chord shortcut handling — skip if user is typing in an input element
      if (chords.length === 0) return;
      if (isTypingTarget(event)) {
        clearChordBuffer();
        return;
      }

      // Only process chord keys that have no modifiers pressed
      const hasModifier = event.ctrlKey || event.metaKey || event.altKey;
      if (hasModifier) {
        clearChordBuffer();
        return;
      }

      if (!event.key || event.key.length !== 1) {
        // Non-printable key (e.g., Escape, ArrowDown) resets chord buffer
        clearChordBuffer();
        return;
      }

      const pressedKey = event.key.toLowerCase();
      const nextBuffer = [...chordBufferRef.current, pressedKey];

      // Check if any chord starts with / matches this buffer
      const activeChords = chords.filter((c) => c.enabled !== false);

      // Check for a full match first
      const fullMatch = activeChords.find(
        (chord) =>
          chord.sequence.length === nextBuffer.length &&
          chord.sequence.every((k, i) => k.toLowerCase() === nextBuffer[i])
      );

      if (fullMatch) {
        if (preventDefault) event.preventDefault();
        clearChordBuffer();
        fullMatch.handler(event);
        return;
      }

      // Check for a partial match (chord in progress)
      const partialMatch = activeChords.some(
        (chord) =>
          chord.sequence.length > nextBuffer.length &&
          chord.sequence
            .slice(0, nextBuffer.length)
            .every((k, i) => k.toLowerCase() === nextBuffer[i])
      );

      if (partialMatch) {
        // Update buffer and reset timeout
        chordBufferRef.current = nextBuffer;
        if (chordTimeoutRef.current !== null) {
          clearTimeout(chordTimeoutRef.current);
        }
        chordTimeoutRef.current = setTimeout(() => {
          clearChordBuffer();
        }, CHORD_TIMEOUT_MS);
      } else {
        // No match at all — clear buffer
        clearChordBuffer();
      }
    },
    [shortcuts, chords, preventDefault, enabled, clearChordBuffer]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearChordBuffer();
    };
  }, [handleKeyDown, enabled, clearChordBuffer]);
}

/**
 * Common keyboard shortcuts for AICA platform
 * Use these constants for consistent shortcuts across the app
 */
export const COMMON_SHORTCUTS = {
  /**
   * Open command palette
   * ⌘K / Ctrl+K
   */
  COMMAND_PALETTE: {
    key: 'k',
    modifiers: { meta: true, ctrl: true },
    description: 'Open command palette',
    displayKey: '⌘K',
  },

  /**
   * Save changes (in forms)
   * ⌘S / Ctrl+S
   */
  SAVE: {
    key: 's',
    modifiers: { meta: true, ctrl: true },
    description: 'Save changes',
    displayKey: '⌘S',
  },

  /**
   * Quick upload
   * ⌘U / Ctrl+U
   */
  UPLOAD: {
    key: 'u',
    modifiers: { meta: true, ctrl: true },
    description: 'Upload records',
    displayKey: '⌘U',
  },

  /**
   * Close modal/sheet
   * Escape
   */
  CLOSE: {
    key: 'Escape',
    description: 'Close modal',
    displayKey: 'Esc',
  },

  /**
   * Search
   * ⌘F / Ctrl+F
   */
  SEARCH: {
    key: 'f',
    modifiers: { meta: true, ctrl: true },
    description: 'Search',
    displayKey: '⌘F',
  },

  /**
   * Go to dashboard
   * ⌘1 / Ctrl+1
   */
  GO_DASHBOARD: {
    key: '1',
    modifiers: { meta: true, ctrl: true },
    description: 'Go to dashboard',
    displayKey: '⌘1',
  },

  /**
   * Go to records
   * ⌘2 / Ctrl+2
   */
  GO_RECORDS: {
    key: '2',
    modifiers: { meta: true, ctrl: true },
    description: 'Go to records',
    displayKey: '⌘2',
  },

  /**
   * Go to projects
   * ⌘3 / Ctrl+3
   */
  GO_PROJECTS: {
    key: '3',
    modifiers: { meta: true, ctrl: true },
    description: 'Go to projects',
    displayKey: '⌘3',
  },

  /**
   * Go to settings
   * ⌘, / Ctrl+,
   */
  GO_SETTINGS: {
    key: ',',
    modifiers: { meta: true, ctrl: true },
    description: 'Go to settings',
    displayKey: '⌘,',
  },

  /**
   * Show keyboard shortcuts help
   * ⌘/ / Ctrl+/
   */
  KEYBOARD_HELP: {
    key: '/',
    modifiers: { meta: true, ctrl: true },
    description: 'Show keyboard shortcuts',
    displayKey: '⌘/',
  },
} as const;

/**
 * Common chord shortcuts for AICA platform (g → letter navigation)
 */
export const COMMON_CHORDS = {
  GO_DASHBOARD: { sequence: ['g', 'd'], description: 'Go to Dashboard' },
  GO_RECORDS: { sequence: ['g', 'r'], description: 'Go to Records' },
  GO_RECORDS_TABLE: { sequence: ['g', 't'], description: 'Go to Records Table' },
  GO_PROJECTS: { sequence: ['g', 'p'], description: 'Go to Projects' },
  GO_SETTINGS: { sequence: ['g', 's'], description: 'Go to Settings' },
  GO_ALERTS: { sequence: ['g', 'l'], description: 'Go to Alerts' },
  GO_BILLING: { sequence: ['g', 'b'], description: 'Go to Billing' },
} as const;

/**
 * Get display key for current platform
 * @param key - Keyboard key
 * @param modifiers - Modifier keys
 * @returns Formatted key string (e.g., "⌘K" on Mac, "Ctrl+K" on Windows)
 */
export function getDisplayKey(
  key: string,
  modifiers?: { meta?: boolean; ctrl?: boolean; shift?: boolean; alt?: boolean }
): string {
  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
  const parts: string[] = [];

  if (modifiers?.meta || modifiers?.ctrl) {
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (modifiers?.shift) {
    parts.push(isMac ? '⇧' : 'Shift');
  }
  if (modifiers?.alt) {
    parts.push(isMac ? '⌥' : 'Alt');
  }

  // Format key name
  const keyName = key === 'Escape' ? 'Esc' : key.toUpperCase();
  parts.push(keyName);

  return isMac ? parts.join('') : parts.join('+');
}
